import { useForm, usePage } from "@inertiajs/react";
import type { FormEvent } from "react";
import { toast } from "sonner";

import backgroundBlue from "@/assets/background_blue.svg";
import AuthCard from "@/components/AuthCard";
import Button from "@/components/Button";
import SearchableSelect, {
  type SelectOption,
} from "@/components/SearchableSelect";
import TextField from "@/components/TextField";
import { contacts } from "@/routes";
import type { SharedProps } from "@/types";

// Same pill look as TextField, for the select trigger.
const SELECT_TRIGGER =
  "w-full rounded-full border-2 border-ink bg-white py-3.5 pl-6 pr-5 font-sans text-base font-light text-ink focus:border-steel focus:outline-none focus:ring-2 focus:ring-steel/30 disabled:cursor-not-allowed disabled:opacity-60";

const SEX_OPTIONS: SelectOption[] = [
  { value: "mujer", label: "Mujer" },
  { value: "hombre", label: "Hombre" },
];

// Mirrors RegistrationsController::SEX_ORIGINS.
const ORIGIN_SEX: Record<string, string> = {
  los_tilos: "mujer",
  la_cuesta: "hombre",
};

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  sex: "",
  subject: "",
  message: "",
};

function RequiredMark() {
  return <span className="text-xl text-ink/60">∗</span>;
}

export default function ContactsNew({ subjects }: { subjects: string[] }) {
  const { currentUser } = usePage<SharedProps>().props;
  const form = useForm({
    ...INITIAL_FORM,
    sex: currentUser ? (ORIGIN_SEX[currentUser.origin] ?? "") : "",
  });
  const subjectOptions: SelectOption[] = subjects.map((subject) => ({
    value: subject,
    label: subject,
  }));

  // The form is noValidate: validation errors surface as toasts (like the
  // server-side flash alerts) instead of the browser's per-field bubbles.
  function validationError(): string | null {
    if (!form.data.name || !form.data.email || !form.data.subject) {
      return "Completa los campos obligatorios: Nombre completo, Correo electrónico y Motivo de contacto.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.data.email)) {
      return "El correo electrónico no es válido.";
    }
    return null;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const error = validationError();
    if (error) {
      toast.error(error, {
        className: "border border-deep-red/40 bg-deep-red/10 text-deep-red",
        duration: 5000,
      });
      return;
    }
    form.transform((data) => ({ contact: data }));
    form.post(contacts.create().url, { onSuccess: () => form.reset() });
  }

  return (
    <AuthCard
      title="Contáctanos"
      subtitle="Si tienes dudas sobre cursos, inscripciones o aportes, escríbenos y te responderemos a la brevedad."
      background={backgroundBlue}
    >
      <form
        onSubmit={submit}
        noValidate
        className="mx-auto flex max-w-xl flex-col gap-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            name="name"
            placeholder="Nombre completo"
            autoComplete="name"
            value={form.data.name}
            onChange={(e) => form.setData("name", e.target.value)}
            adornment={<RequiredMark />}
            required
          />
          <TextField
            name="email"
            type="email"
            placeholder="Correo electrónico"
            autoComplete="email"
            value={form.data.email}
            onChange={(e) => form.setData("email", e.target.value)}
            adornment={<RequiredMark />}
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            name="phone"
            type="tel"
            placeholder="Teléfono"
            autoComplete="tel"
            value={form.data.phone}
            onChange={(e) => form.setData("phone", e.target.value)}
          />
          <SearchableSelect
            value={form.data.sex}
            onChange={(value) => form.setData("sex", value)}
            options={SEX_OPTIONS}
            placeholder="Sexo"
            className={SELECT_TRIGGER}
            disabled={!!currentUser}
          />
        </div>

        <SearchableSelect
          value={form.data.subject}
          onChange={(value) => form.setData("subject", value)}
          options={subjectOptions}
          placeholder="Motivo de contacto ∗"
          className={SELECT_TRIGGER}
          required
        />

        <textarea
          name="message"
          placeholder="Mensaje"
          rows={6}
          value={form.data.message}
          onChange={(e) => form.setData("message", e.target.value)}
          className="w-full resize-none rounded-3xl border-2 border-ink bg-white px-6 py-4 font-sans text-base font-light text-ink placeholder:text-ink/50 focus:border-steel focus:outline-none focus:ring-2 focus:ring-steel/30"
        />

        <Button
          type="submit"
          disabled={form.processing}
          className="mt-2 w-full"
        >
          Enviar
        </Button>
      </form>
    </AuthCard>
  );
}
